import { FC, useContext, useMemo, useRef, useState } from "react";
import { PieChart, Pie, PieSectorShapeProps, Sector, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ChartDataEntry, downloadChartAsPng, formatOtherCategoryExplanation, getChartColors, OTHER_SLICE_LABEL, parseChartEntries, PIE_OTHER_THRESHOLD } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatChartHelper";
import { AiChatMessage } from "@app/projects/network-assessment/provider-hub/interfaces";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { formatDate, formatDateCustom, formatDateTime } from "@app/shared/formatDateTime";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { Modal } from "@app/shared/Modals";
import { CancelButtonType } from "@app/shared/Modals/modalContent";
import { TooltipPlacement } from "@app/shared/literals";
import { QESButton } from "@app/shared/qes-standards/Button";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { useMeasuredSize } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useMeasuredSize";

interface PieDataEntry {
  /** Labels folded into this entry, largest value first. Only set on the "Other" entry. */
  collapsedLabels?: string[];
  label: string;
  percent: number;
  value: number;
}

interface AIChatPieChartProps {
  /** Whether the chat is expanded to full screen, which moves the legend away from the right edge. */
  isFullScreen: boolean;
  message: AiChatMessage;
}

const MODAL_OTHER_THRESHOLD = 0.02;
const CHART_COLORS = getChartColors();

const LEGEND_CHAR_WIDTH_PX = 8;
const LEGEND_SWATCH_WIDTH_PX = 14; // 10px swatch + 4px gap
const LEGEND_COLUMN_BUFFER_PX = 24; // margin for font-metric estimation error
const MIN_LEGEND_COLUMN_WIDTH_PX = 140;
/** The pie is scrolled to rather than shrunk once the legend leaves less than this. */
const MIN_MODAL_PIE_HEIGHT_PX = 240;

const formatLegendEntryText = (entry: PieDataEntry): string =>
  `${entry.label}: ${(entry.percent * 100).toFixed(1)}% (${entry.value})`;

// Sizes legend columns to the longest label so entries never wrap or get cut off.
export const computeModalLegendColumnWidth = (slices: PieDataEntry[]): number => {
  if (slices.length === 0) return MIN_LEGEND_COLUMN_WIDTH_PX;
  const maxTextLength = Math.max(...slices.map(entry => formatLegendEntryText(entry).length));
  const estimatedWidth = LEGEND_SWATCH_WIDTH_PX + maxTextLength * LEGEND_CHAR_WIDTH_PX + LEGEND_COLUMN_BUFFER_PX;
  return Math.max(MIN_LEGEND_COLUMN_WIDTH_PX, estimatedWidth);
};

/**
 * Height for the modal pie: whatever the meta block and legend leave, but never so little that
 * the pie stops being readable. Past that floor the modal scrolls instead of shrinking the plot.
 */
export const computeModalPieHeightPx = (
  scrollHeightPx: number,
  metaHeightPx: number,
  footerHeightPx: number
): number => Math.max(
  MIN_MODAL_PIE_HEIGHT_PX,
  // Floored so a fractional remainder cannot round the content past the box and leave a
  // permanent 1px scrollbar when everything actually fits.
  Math.floor(scrollHeightPx - metaHeightPx - footerHeightPx)
);

export const buildSlices = (message: AiChatMessage, threshold: number): PieDataEntry[] => {
  if (!message.content || message.content.length < 2) return [];

  const numericValues = parseChartEntries(message.content);

  const total = numericValues.reduce((sum, entry) => sum + entry.value, 0);
  const slices: PieDataEntry[] = [];
  const collapsed: ChartDataEntry[] = [];

  for (const entry of numericValues) {
    const percent = total === 0 ? 0 : entry.value / total;
    if (percent < threshold) {
      collapsed.push(entry);
    } else {
      slices.push({ label: entry.label, percent, value: entry.value });
    }
  }

  const otherValue = collapsed.reduce((sum, entry) => sum + entry.value, 0);
  if (otherValue > 0) {
    slices.push({
      // Largest first so the tooltip's cap keeps the biggest contributors
      collapsedLabels: [...collapsed].sort((a, b) => b.value - a.value).map(entry => entry.label),
      label: OTHER_SLICE_LABEL,
      percent: total === 0 ? 0 : otherValue / total,
      value: otherValue,
    });
  }

  return slices;
};

const tooltipFormatter = (value: ValueType, name: NameType, item: Payload<ValueType, NameType>) => {
  const payload = item.payload as PieDataEntry | undefined;
  return [`${((payload?.percent ?? 0) * 100).toFixed(1)}% (${value})`, name];
};

// The "Other" wedge hides which categories it swallowed, so the inline legend carries the standard
// QES info affordance explaining the collapsed set. The modal legend doesn't need this — the modal
// already states the same explanation statically (see the "Other" note below the modal chart).
// Composed from TooltipWrapper + QesIcon rather than InfoIndicator so containerClassName can reach
// the flex item itself — InfoIndicator only exposes a class on the innermost div, which leaves the
// icon baseline-aligned and sitting low against the legend text.
const renderOtherCategoryInfo = (entry: PieDataEntry, threshold: number, position: TooltipPlacement) =>
  entry.label === OTHER_SLICE_LABEL && !!entry.collapsedLabels?.length ? (
    <TooltipWrapper
      containerClassName="ai-chat-chart__other-info"
      position={position}
      text={formatOtherCategoryExplanation(entry.collapsedLabels, threshold)}
      tooltipWidth="320px"
    >
      <QesIcon iconName={QesIcons.InfoCircle} description={`What is included in ${OTHER_SLICE_LABEL}`} width="14" />
    </TooltipWrapper>
  ) : null;

// TooltipWrapper centres a "top" tooltip on its trigger and has no viewport collision handling, so
// a 320px box needs 160px of clearance on each side. In the docked rail that clearance doesn't
// exist — $ai-chat-width is clamp(16rem, 40vw - 15rem, 40rem), so the rail is often narrower than
// the tooltip itself and sits flush against the right edge of the screen, which cut the text off.
// Opening to the left instead puts the whole box over the page content beside the rail, where there
// is always room. Full screen is the opposite case: the legend is capped at 40rem from the left of
// the content area, so there the box would run off the *left* edge and "top" is the safe choice.
export const getOtherInfoPlacement = (isFullScreen: boolean): TooltipPlacement => isFullScreen ? "top" : "left";

function renderPieLegend(slices: PieDataEntry[], otherInfoPlacement: TooltipPlacement) {
  return (
    <div style={{
      display: "flex",
      flexDirection: slices.length <= 5 ? "column" : "row",
      flexWrap: "wrap",
      gap: slices.length <= 5 ? "4px" : "4px 16px",
      paddingLeft: "10px",
      paddingTop: "8px"
    }}>
      {slices.map((entry, index) => (
        <span key={entry.label} style={{ alignItems: "center", display: "flex", gap: "4px", whiteSpace: "nowrap" }}>
        <span style={{
          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
          display: "inline-block",
          flexShrink: 0,
          height: "10px",
          width: "10px"
        }} />
          {entry.label}: {(entry.percent * 100).toFixed(1)}% ({entry.value})
          {renderOtherCategoryInfo(entry, PIE_OTHER_THRESHOLD, otherInfoPlacement)}
      </span>
      ))}
    </div>
  )
}

function renderModalPieLegend(slices: PieDataEntry[], columnWidthPx: number) {
  return (
    <div className="ai-chat-chart__modal-legend"
         style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${columnWidthPx}px, 1fr))` }}>
      {slices.map((entry, index) => (
        <span key={entry.label} className="ai-chat-chart__modal-legend-item">
        <span style={{
          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
          display: "inline-block",
          flexShrink: 0,
          height: "10px",
          width: "10px"
        }} />
          {formatLegendEntryText(entry)}
      </span>
      ))}
    </div>
  )
}

const AIChatPieChart: FC<AIChatPieChartProps> = ({ isFullScreen, message }) => {
  const featureFlagContext = useContext(FeatureFlagContext);
  const projectContext = useContext(CountyProjectContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const slices = useMemo(() => buildSlices(message, PIE_OTHER_THRESHOLD), [message.content]);
  const modalSlices = useMemo(() => buildSlices(message, MODAL_OTHER_THRESHOLD), [message.content]);
  const modalLegendColumnWidth = useMemo(() => computeModalLegendColumnWidth(modalSlices), [modalSlices]);
  // The source data can contain a category literally named "Other", which buildSlices keeps as an
  // ordinary slice ahead of the aggregate it appends. Match on collapsedLabels — the marker only the
  // generated slice carries — so the note below describes the collapsed set rather than finding the
  // source slice and silently rendering nothing.
  const modalOtherSlice = modalSlices.find(slice => !!slice.collapsedLabels?.length);

  // The legend is rendered outside the PieChart (see below), so its height is independent of the
  // pie's — measuring both to divide the box between them cannot feed back into itself.
  const [modalScrollElement, setModalScrollElement] = useState<HTMLDivElement | null>(null);
  const [modalMetaElement, setModalMetaElement] = useState<HTMLDivElement | null>(null);
  const [modalFooterElement, setModalFooterElement] = useState<HTMLDivElement | null>(null);
  const modalScrollSize = useMeasuredSize(modalScrollElement);
  const modalMetaSize = useMeasuredSize(modalMetaElement);
  const modalFooterSize = useMeasuredSize(modalFooterElement);
  const modalPieHeightPx = computeModalPieHeightPx(
    modalScrollSize.heightPx, modalMetaSize.heightPx, modalFooterSize.heightPx);

  const handleDownloadChart = async () => {
    if (!chartRef.current) return;
    setIsDownloading(true);
    try {
      await downloadChartAsPng(chartRef.current, `quinn-chart-${formatDateCustom(new Date(), 'yyyyMMDD_HHmmss')}.png`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!featureFlagContext.enabledFeatureFlags.has(Features.AI_CHARTING_ENABLED)) {
    return <p className="ai-chat-panel__message__response">Sorry, something went wrong. Please contact support.</p>;
  }

  if (!message.content || message.content.length < 2) return null;

  return (
    <>
      {!!message.chartHeader && (
        <p className="ai-chat-chart__header-title">{message.chartHeader}</p>
      )}
      <ResponsiveContainer width="100%" height={450}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="label"
            shape={(shapeProps: PieSectorShapeProps) => (
              <Sector {...shapeProps} fill={CHART_COLORS[shapeProps.index % CHART_COLORS.length]} />
            )}
          />
          <Tooltip formatter={tooltipFormatter} itemStyle={{ color: "#000" }} />
          {slices.length <= 10 && (
            <Legend content={renderPieLegend(slices, getOtherInfoPlacement(isFullScreen))} />
          )}
        </PieChart>
      </ResponsiveContainer>
      <QESButton onClick={() => setModalOpen(true)} qesTestId="ai-chat-chart__open-modal" qesType="secondary">View
        Full Chart</QESButton>
      <Modal
        className="ai-chat-chart-modal"
        closeModal={() => setModalOpen(false)}
        isVisible={modalOpen}
        modalHeight={70}
        modalWidth={70}
        content={{
          body: modalOpen ? (
            <>
              {/* Outside the scroll wrapper, and so outside the html2canvas capture root: it is
                  absolutely positioned above the container's top edge and would otherwise be
                  clipped by the scroller or hold it permanently scrolled. */}
              <div className="ai-chat-chart__header" data-html2canvas-ignore="true">
                <QESButton qesType="secondary"
                           qesTestId="ai-chat-chart__download"
                           disabled={isDownloading}
                           onClick={handleDownloadChart}>
                  {isDownloading ? 'Downloading...' : 'Download Chart'}
                </QESButton>
              </div>
              {/* The scroller must stay OUTSIDE the capture root below: html2canvas roots its
                  parse at the element it is given, so an ancestor's overflow never clips the
                  capture, but a scroller inside it would. Keeps the exported PNG complete when
                  a long legend pushes the content past the modal. */}
              <div className="ai-chat-chart__modal-scroll" ref={setModalScrollElement}>
                <div className="ai-chat-chart__container" ref={chartRef}>
                  <div className="ai-chat-chart__meta" ref={setModalMetaElement}>
                    {!!message.chartHeader && (
                      <p className="ai-chat-chart__header-title ai-chat-chart__header-title-modal">{message.chartHeader}</p>
                    )}
                    <div className="ai-chat-chart__date-row" data-testid="ai-chat-chart__date-row">
                      <span><strong>Chart Created On:</strong> {formatDate(new Date())}</span>
                      <span><strong>Timeslice Date:</strong> {formatDateTime(projectContext.runConfiguration.dateCreated)}</span>
                    </div>
                  </div>
                  <div className="ai-chat-chart__modal-chart" data-testid="ai-chat-chart__modal-chart">
                    <ResponsiveContainer width="100%" height={modalPieHeightPx}>
                      <PieChart>
                        <Pie
                          data={modalSlices}
                          dataKey="value"
                          nameKey="label"
                          shape={(shapeProps: PieSectorShapeProps) => (
                            <Sector {...shapeProps} fill={CHART_COLORS[shapeProps.index % CHART_COLORS.length]} />
                          )}
                        />
                        <Tooltip formatter={tooltipFormatter} itemStyle={{ color: "#000" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* The legend sits outside the PieChart on purpose. As a recharts <Legend> it is
                      laid out inside the chart's height budget, so a tall legend shrinks the pie
                      toward nothing; as a sibling it takes what it needs and the modal scrolls. */}
                  <div className="ai-chat-chart__modal-footer" ref={setModalFooterElement}>
                    {renderModalPieLegend(modalSlices, modalLegendColumnWidth)}
                    {/* A hover tooltip can't survive the PNG export, so the modal also states this
                        statically. Deliberately not data-html2canvas-ignore'd. */}
                    {!!modalOtherSlice?.collapsedLabels?.length && (
                      <p className="ai-chat-chart__other-note" data-testid="ai-chat-chart__other-note">
                        <strong>Other:</strong>{" "}
                        {formatOtherCategoryExplanation(modalOtherSlice.collapsedLabels, MODAL_OTHER_THRESHOLD)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null,
          cancelButtonText: "",
          cancelButtonType: CancelButtonType.TopRight,
          // Modal API requires these even for close-only dialogs
          confirmButtonAction: () => {
          },
          confirmButtonText: "",
          title: "Quinn Pie Chart",
        }}
      />
    </>
  );
};

export default AIChatPieChart;
